import { useState } from 'react'
import { Button } from '@design-systems/canvas-ui'
import Icon, {
  CheckmarkIcon,
  PencilIcon,
  WarningIcon,
} from '@design-systems/react-icons'
import {
  getSectionStatusLabel,
  isSectionApproved,
  isSectionConflicted,
  isSectionLocked,
  isSectionSettled,
  SECTION_CONFLICT_PROMPT,
} from '../config/draftReviewConfig'
import {
  countGroupSections,
  getSectionValue,
  spaceLineBreaks,
  isGroupAllApproved,
} from '../helpers/draftReviewHelpers'

// One section group as a single card: every section is a row, and the group
// is edited as a whole and approved in one write.
const DraftGroupCard = ({
  bulkDrafts = null,
  group,
  isReadOnly,
  isSaving,
  onApprove,
  onBulkChange,
  onEdit,
  onResolve,
  onUndo,
}) => {
  // `null` while not editing, otherwise the edited text keyed by section id.
  const [drafts, setDrafts] = useState(null)
  // Set by an Approve click that found an empty section.
  const [isMissingContent, setIsMissingContent] = useState(false)
  const { approved, total } = countGroupSections(group)
  const undecided = group.sections.filter(
    ({ status }) => !isSectionSettled(status),
  )
  const undoable = group.sections.filter(
    ({ status }) => isSectionSettled(status) && !isSectionLocked(status),
  )
  const sources = [
    ...new Set(group.sections.map(({ sourceLabel }) => sourceLabel)),
  ]
    .filter(Boolean)
    .join(' · ')
  const conflictCount = group.sections.filter(({ status }) =>
    isSectionConflicted(status),
  ).length
  const hasConflict = conflictCount > 0
  const isEditing = drafts !== null
  const hasEmptySection = undecided.some((section) => !getSectionValue(section))
  const showMissingContent = isMissingContent && hasEmptySection
  const tone = isGroupAllApproved(group)
    ? 'approved'
    : hasConflict
      ? 'warning'
      : 'neutral'

  const startEditing = () =>
    setDrafts(
      Object.fromEntries(
        undecided.map((section) => [
          section.id,
          spaceLineBreaks(getSectionValue(section)),
        ]),
      ),
    )

  // Saving only holds the edits on screen; Approve is what sends them.
  const saveChanges = () => {
    onEdit(drafts)
    setDrafts(null)
  }

  // Every section needs content; the API refuses to approve an empty one.
  const approve = () => {
    setIsMissingContent(hasEmptySection)
    if (!hasEmptySection) onApprove(undecided)
  }

  const renderActions = () => {
    // The review is over: nothing is left to act on.
    if (isReadOnly) {
      return (
        <Button className="draft-section__approved-button" isDisabled>
          <span className="upload-documents__button-content">
            <Icon size="sm" svg={CheckmarkIcon} /> Approved
          </span>
        </Button>
      )
    }
    const editButtons = isEditing ? (
      <>
        <Button onClick={() => setDrafts(null)} variant="secondary">
          Cancel
        </Button>
        <Button
          isDisabled={
            isSaving || Object.values(drafts).some((value) => !value.trim())
          }
          onClick={saveChanges}
          variant="secondary"
        >
          <span className="upload-documents__button-content">
            <Icon size="sm" svg={CheckmarkIcon} /> Save changes
          </span>
        </Button>
      </>
    ) : (
      <Button isDisabled={isSaving} onClick={startEditing} variant="secondary">
        <span className="upload-documents__button-content">
          <Icon size="sm" svg={PencilIcon} /> Edit
        </span>
      </Button>
    )
    if (undecided.length > 0) {
      return (
        <>
          {editButtons}
          {/* Held back while editing, so unsaved text is never approved. */}
          <Button
            isDisabled={isSaving || isEditing}
            onClick={approve}
            variant="primary"
          >
            <span className="upload-documents__button-content">
              <Icon size="sm" svg={CheckmarkIcon} /> Approve
            </span>
          </Button>
        </>
      )
    }
    return (
      <>
        {undoable.length > 0 && !bulkDrafts && (
          <button
            className="draft-section__link"
            disabled={isSaving}
            onClick={() => onUndo(undoable)}
            type="button"
          >
            Undo
          </button>
        )}
        {isGroupAllApproved(group) && !bulkDrafts && (
          <Button className="draft-section__approved-button" isDisabled>
            <span className="upload-documents__button-content">
              <Icon size="sm" svg={CheckmarkIcon} /> Approved
            </span>
          </Button>
        )}
      </>
    )
  }

  const renderValue = (section) => {
    if (bulkDrafts && section.id in bulkDrafts) {
      return (
        <textarea
          aria-label={`Edit ${section.title}`}
          onChange={({ target }) => onBulkChange(section.id, target.value)}
          rows={2}
          value={bulkDrafts[section.id]}
        />
      )
    }
    if (isEditing && section.id in drafts) {
      return (
        <textarea
          aria-label={`Edit ${section.title}`}
          onChange={({ target }) =>
            setDrafts((current) => ({ ...current, [section.id]: target.value }))
          }
          rows={2}
          value={drafts[section.id]}
        />
      )
    }
    if (isSectionConflicted(section.status) && !isReadOnly) {
      return (
        <div className="draft-section__conflict">
          <p className="draft-section__conflict-prompt">
            <Icon size="sm" svg={WarningIcon} />
            <span>
              <strong>
                {section.candidates.length} conflicting values found.
              </strong>{' '}
              {SECTION_CONFLICT_PROMPT}
            </span>
          </p>
          <ul>
            {section.candidates.map((candidate) => (
              <li className="draft-section__candidate" key={candidate.id}>
                <div>
                  <strong>
                    Option {candidate.optionNumber}
                    {candidate.sourceFileName &&
                      ` · ${candidate.sourceFileName}`}
                  </strong>
                  <span>{spaceLineBreaks(candidate.value)}</span>
                </div>
                <Button
                  className="draft-section__use-button"
                  isDisabled={isSaving}
                  onClick={() =>
                    onResolve(section.id, { candidateId: candidate.id })
                  }
                  variant="primary"
                >
                  Use this value
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )
    }
    const value = getSectionValue(section)
    return (
      <>
        {value ? (
          spaceLineBreaks(value)
        ) : (
          <span
            className={`draft-group__empty${
              showMissingContent ? ' draft-group__empty--error' : ''
            }`}
          >
            No value extracted — use Edit to add one.
          </span>
        )}
        {isSectionSettled(section.status) &&
          !isSectionApproved(section.status) && (
            <span className="draft-section__chip draft-section__chip--warning">
              {getSectionStatusLabel(section.status)}
            </span>
          )}
      </>
    )
  }

  return (
    <article
      aria-label={group.label}
      className={`draft-section draft-section--${tone}${
        isEditing ? ' draft-section--editing' : ''
      }`}
    >
      <header className="draft-section__header">
        <div className="draft-section__heading">
          <strong>{group.label}</strong>
          {sources && <small>{sources}</small>}
        </div>
        <div className="draft-section__actions">
          {hasConflict && !isReadOnly && (
            <span className="draft-section__chip draft-section__chip--warning">
              {conflictCount} to resolve
            </span>
          )}
          <span className="draft-group__count">
            {approved} / {total} approved
          </span>
          {renderActions()}
        </div>
      </header>
      {showMissingContent && (
        <p className="draft-group__error" role="alert">
          Some sections are empty. Add content with Edit, or pick an option,
          before approving.
        </p>
      )}
      <dl className="draft-group__rows">
        {group.sections.map((section) => (
          <div
            className={
              isSectionConflicted(section.status) && !isReadOnly
                ? 'draft-group__row--conflict'
                : undefined
            }
            key={section.id}
          >
            <dt>
              {isSectionConflicted(section.status) && (
                <Icon size="sm" svg={WarningIcon} />
              )}
              {section.title}
            </dt>
            <dd>{renderValue(section)}</dd>
          </div>
        ))}
      </dl>
    </article>
  )
}

export default DraftGroupCard
